import { PropsWithChildren, useMemo, useReducer } from "react";

import { useMaxPastFetchesCount } from "../../../../hooks/use_max_past_fetches_count";
import {
  PastDatesApi,
  PastDatesApiContext,
  PastDatesState,
  PastDatesStateContext
} from "./past_dates_context";

type PastDatesActions =
  | { type: "incrementPastFetchesCount"; maxPastFetchesCount: number }
  | { type: "setCanScrollInPast"; canScrollInPast: boolean };

const pastDatesReducer = (state: PastDatesState, action: PastDatesActions): PastDatesState => {
  switch (action.type) {
    case "incrementPastFetchesCount": {
      const { maxPastFetchesCount } = action;
      const incrementedValue = state.pastFetchesCount + 1;
      return {
        ...state,
        canScrollInPast: incrementedValue < maxPastFetchesCount,
        pastFetchesCount:
          state.pastFetchesCount < maxPastFetchesCount ? incrementedValue : state.pastFetchesCount
      };
    }
    case "setCanScrollInPast": {
      return {
        ...state,
        canScrollInPast: action.canScrollInPast
      };
    }
  }
};

const usePastDatesReducer = (initialPastFetchesCount: number) =>
  useReducer(pastDatesReducer, {
    pastFetchesCount: initialPastFetchesCount,
    canScrollInPast: false
  });

type UsePastDatesReturn = {
  pastDatesApi: PastDatesApi;
  pastDatesStateValue: PastDatesState;
};

const usePastDates = (): UsePastDatesReturn => {
  const { maxPastFetchesCount, rangeMonthsDuration } = useMaxPastFetchesCount();
  const [pastDatesState, pastDatesDispatch] = usePastDatesReducer(rangeMonthsDuration > 0 ? 1 : 0);

  const pastDatesApi = useMemo<PastDatesApi>(() => {
    const incrementPastFetchesCount = () => {
      pastDatesDispatch({
        type: "incrementPastFetchesCount",
        maxPastFetchesCount
      });
    };

    const setCanScrollInPast = (canScrollInPast: boolean) => {
      pastDatesDispatch({
        type: "setCanScrollInPast",
        canScrollInPast
      });
    };

    return {
      incrementPastFetchesCount,
      setCanScrollInPast
    };
  }, []);

  const pastDatesStateValue = useMemo(() => pastDatesState, [pastDatesState]);

  return {
    pastDatesApi,
    pastDatesStateValue
  };
};

export const PastDatesContextProvider = ({ children }: PropsWithChildren) => {
  const { pastDatesApi, pastDatesStateValue } = usePastDates();

  return (
    <PastDatesApiContext.Provider value={pastDatesApi}>
      <PastDatesStateContext.Provider value={pastDatesStateValue}>{children}</PastDatesStateContext.Provider>
    </PastDatesApiContext.Provider>
  );
};
